import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AnalyticsEnvelope,
  AnalyticsPeriod,
  Granularity,
  KycSubjectSummary,
  OperatorSummary,
  PaymentSummary,
  RatingSummary,
  RentalSummary,
  SeriesPoint,
  SettlementSummary,
  UserSummary,
  VehicleSummary,
  WalletSummary,
} from '../types/analytics.types';

/**
 * Lecture d'**analytics-service** (gateway `/analytics`, base controller
 * `/api/analytics` → strip du 1er segment).
 *
 * Le **scope est dérivé du JWT** côté back : on n'envoie **jamais** de
 * `scopeType`/`scopeRef` ici (le drill-down admin est un cas séparé, hors de ce
 * service self-scoped). Chaque app n'appelle que les endpoints dont elle
 * dispose en slice 0 (owner = règlements + notes ; operator = règlements).
 *
 * ⚠️ **403 = acteur non résolvable** (créé avant analytics, backfill non passé) :
 * ce n'est PAS une erreur générique. Utiliser {@link isUnresolvedScope} au call
 * site pour afficher un message dédié plutôt que de router vers `toErrorMessage`.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/analytics/api/analytics`;

  private range(period: AnalyticsPeriod): HttpParams {
    return new HttpParams().set('from', period.from).set('to', period.to);
  }

  private unwrap<T>(url: string, params: HttpParams): Observable<T> {
    return this.http
      .get<AnalyticsEnvelope<T>>(url, { params })
      .pipe(map((env) => env.data));
  }

  paymentSummary(period: AnalyticsPeriod): Observable<PaymentSummary> {
    return this.unwrap<PaymentSummary>(`${this.base}/payments/summary`, this.range(period));
  }

  settlementSummary(period: AnalyticsPeriod): Observable<SettlementSummary> {
    return this.unwrap<SettlementSummary>(`${this.base}/settlements/summary`, this.range(period));
  }

  settlementSeries(
    period: AnalyticsPeriod,
    granularity: Granularity = period.granularity,
  ): Observable<SeriesPoint[]> {
    const params = this.range(period).set('granularity', granularity);
    return this.unwrap<SeriesPoint[]>(`${this.base}/settlements/series`, params);
  }

  ratingSummary(period: AnalyticsPeriod): Observable<RatingSummary> {
    return this.unwrap<RatingSummary>(`${this.base}/ratings/summary`, this.range(period));
  }

  /**
   * `GET /rentals/summary` — funnel cycle de vie + annulations + retards + GMV
   * réservée. Même URL pour les 3 acteurs (scope dérivé du JWT).
   */
  rentalSummary(period: AnalyticsPeriod): Observable<RentalSummary> {
    return this.unwrap<RentalSummary>(`${this.base}/rentals/summary`, this.range(period));
  }

  /**
   * `GET /rentals/series` — réservations créées dans le temps (`count`) + GMV
   * réservée (`value`). `SeriesPoint` réutilisé tel quel.
   */
  rentalSeries(
    period: AnalyticsPeriod,
    granularity: Granularity = period.granularity,
  ): Observable<SeriesPoint[]> {
    const params = this.range(period).set('granularity', granularity);
    return this.unwrap<SeriesPoint[]>(`${this.base}/rentals/series`, params);
  }

  /**
   * `GET /vehicles/summary` — parc à l'instant t (total / actifs / en revue /
   * inactifs / `byStatus`) **+** `newVehicles` sur la période. Même URL pour les
   * 3 acteurs (scope JWT).
   */
  vehicleSummary(period: AnalyticsPeriod): Observable<VehicleSummary> {
    return this.unwrap<VehicleSummary>(`${this.base}/vehicles/summary`, this.range(period));
  }

  /**
   * `GET /vehicles/series` — acquisition (véhicules créés) dans le temps
   * (`count` ; `value` toujours 0). `SeriesPoint` réutilisé tel quel.
   */
  vehicleSeries(
    period: AnalyticsPeriod,
    granularity: Granularity = period.granularity,
  ): Observable<SeriesPoint[]> {
    const params = this.range(period).set('granularity', granularity);
    return this.unwrap<SeriesPoint[]>(`${this.base}/vehicles/series`, params);
  }

  /**
   * `GET /wallet/summary` — flux de trésorerie par type de compte
   * (entrées/sorties/net) sur la période. Scope JWT (owner/operator = leur flux,
   * admin = plateforme).
   */
  walletSummary(period: AnalyticsPeriod): Observable<WalletSummary> {
    return this.unwrap<WalletSummary>(`${this.base}/wallet/summary`, this.range(period));
  }

  /**
   * `GET /wallet/series` — flux **net** dans le temps (`value`, **peut être
   * négatif**). `SeriesPoint` réutilisé tel quel.
   */
  walletSeries(
    period: AnalyticsPeriod,
    granularity: Granularity = period.granularity,
  ): Observable<SeriesPoint[]> {
    const params = this.range(period).set('granularity', granularity);
    return this.unwrap<SeriesPoint[]>(`${this.base}/wallet/series`, params);
  }

  /**
   * `GET /kyc/summary` — **admin-only** (403 pour un token non-admin : ne PAS
   * appeler côté owner/operator). Renvoie un **tableau**, une entrée par type de
   * sujet (CUSTOMER / OPERATOR).
   */
  kycSummary(period: AnalyticsPeriod): Observable<KycSubjectSummary[]> {
    return this.unwrap<KycSubjectSummary[]>(`${this.base}/kyc/summary`, this.range(period));
  }

  /** `GET /users/summary` — **admin-only** (base active + acquisition/pertes). */
  usersSummary(period: AnalyticsPeriod): Observable<UserSummary> {
    return this.unwrap<UserSummary>(`${this.base}/users/summary`, this.range(period));
  }

  /** `GET /users/series` — **admin-only** — acquisition (`count`). */
  usersSeries(
    period: AnalyticsPeriod,
    granularity: Granularity = period.granularity,
  ): Observable<SeriesPoint[]> {
    const params = this.range(period).set('granularity', granularity);
    return this.unwrap<SeriesPoint[]>(`${this.base}/users/series`, params);
  }

  /** `GET /operators/summary` — **admin-only** (parc opérateurs + acquisition). */
  operatorsSummary(period: AnalyticsPeriod): Observable<OperatorSummary> {
    return this.unwrap<OperatorSummary>(`${this.base}/operators/summary`, this.range(period));
  }

  /** `GET /operators/series` — **admin-only** — acquisition (`count`). */
  operatorsSeries(
    period: AnalyticsPeriod,
    granularity: Granularity = period.granularity,
  ): Observable<SeriesPoint[]> {
    const params = this.range(period).set('granularity', granularity);
    return this.unwrap<SeriesPoint[]>(`${this.base}/operators/series`, params);
  }
}

/**
 * `true` si l'erreur est un **403 de scope non résolvable** — à distinguer d'un
 * échec réseau ou serveur. Le back renvoie 403 quand l'acteur n'a pas d'entrée
 * analytics (pas encore backfillé) : c'est un état transitoire attendu, pas un
 * bug, et il mérite un message dédié.
 */
export function isUnresolvedScope(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 403;
}
